import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "../../redux/network/https";
import { endPoints, queryKeys } from "../../redux/network/constant";

export const CONTRACT_LINK_REFRESH_INTERVAL_MS = 10 * 60 * 1000;

const fetchDraftFlowSessions = async ({ queryKey }) => {
  const [, , projectId] = queryKey;
  const resolvedProjectId = projectId === "all" ? null : projectId;
  const { data } = await axiosInstance.get(endPoints.AIChatSessions(resolvedProjectId));
  return data?.data;
};

export const useDraftFlowSessions = (projectId, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.AIChatSessions(projectId || "all"),
    queryFn: fetchDraftFlowSessions,
    enabled,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 20 * 1000,
  });
};

const postCreateDraftFlowSession = async ({ payload, params }) => {
  const { data } = await axiosInstance.post(
    endPoints.AIChatSessions(null, params),
    payload,
  );
  return data?.data;
};

export const useCreateDraftFlowSession = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postCreateDraftFlowSession,
    onSuccess: (_data, variables) => {
      const projectId = variables?.payload?.project_id;
      if (projectId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.AIChatSessions(projectId),
        });
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.AIChatSessions("all"),
      });
    },
  });
};

const fetchDraftFlowSessionDetail = async ({ queryKey }) => {
  const [, , sessionId] = queryKey;
  const { data } = await axiosInstance.get(
    endPoints.AIChatSessionDetail(sessionId),
  );
  return data?.data;
};

export const useDraftFlowSessionDetail = (sessionId, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.AIChatSession(sessionId),
    queryFn: fetchDraftFlowSessionDetail,
    enabled: !!sessionId && enabled,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
};

const patchDraftFlowSession = async ({ sessionId, payload }) => {
  const { data } = await axiosInstance.patch(
    endPoints.AIChatSessionDetail(sessionId),
    payload,
  );
  return data?.data;
};

export const useUpdateDraftFlowSession = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: patchDraftFlowSession,
    onSuccess: (_data, variables) => {
      const sessionId = variables?.sessionId;
      if (sessionId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.AIChatSession(sessionId),
        });
      }
      const projectId = variables?.projectId;
      if (projectId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.AIChatSessions(projectId),
        });
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.AIChatSessions("all"),
      });
    },
  });
};

const fetchDraftFlowMessages = async (sessionId, page, pageSize) => {
  const { data } = await axiosInstance.get(endPoints.AIChatMessages(sessionId), {
    params: { page, page_size: pageSize },
  });
  const responseData = data?.data;
  return responseData;
};

export const useDraftFlowMessages = (sessionId, page, pageSize, enabled = true) => {
  return useQuery({
    queryKey: [...queryKeys.AIChatMessages(sessionId, page), pageSize],
    queryFn: () => fetchDraftFlowMessages(sessionId, page, pageSize),
    enabled: !!sessionId && enabled,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    staleTime: 10 * 1000,
  });
};

const postDraftFlowMessage = async ({ sessionId, payload, signal }) => {
  const { data } = await axiosInstance.post(
    endPoints.AIChatMessages(sessionId),
    payload,
    { signal },
  );
  return data?.data;
};

export const useSendDraftFlowMessage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postDraftFlowMessage,
    onSuccess: (_data, variables) => {
      const sessionId = variables?.sessionId;
      if (sessionId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.AIChatSession(sessionId),
        });
      }
    },
  });
};

const postStopDraftFlowSession = async (sessionId) => {
  const { data } = await axiosInstance.post(
    endPoints.AIChatStop(sessionId),
    {},
  );
  return data?.data;
};

export const useStopDraftFlowSession = () => {
  return useMutation({
    mutationFn: postStopDraftFlowSession,
  });
};

const postGenerateDraftFlowContract = async ({ sessionId, payload }) => {
  const { data } = await axiosInstance.post(
    endPoints.AIChatGenerateContract(sessionId),
    payload,
  );
  return data?.data;
};

export const useGenerateDraftFlowContract = () => {
  return useMutation({
    mutationFn: postGenerateDraftFlowContract,
  });
};

const fetchDraftFlowContract = async ({ queryKey }) => {
  const [, , contractId] = queryKey;
  const { data } = await axiosInstance.get(
    endPoints.AIChatContractDetail(contractId),
  );
  return data?.data;
};

export const useDraftFlowContract = (contractId, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.AIChatContract(contractId),
    queryFn: fetchDraftFlowContract,
    enabled: !!contractId && enabled,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 10 * 1000,
    refetchInterval: CONTRACT_LINK_REFRESH_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });
};

const fetchAIChatGrant = async ({ queryKey }) => {
  const [, , sessionId] = queryKey;
  const { data } = await axiosInstance.get(endPoints.AIChatGrant(sessionId));
  return data?.data ?? null;
};

export const useAIChatGrant = (sessionId, enabled = true) =>
  useQuery({
    queryKey: queryKeys.AIChatGrant(sessionId),
    queryFn: fetchAIChatGrant,
    enabled: !!sessionId && enabled,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

const invalidateGrantState = (queryClient, sessionId) => {
  if (!sessionId) return;
  queryClient.invalidateQueries({ queryKey: queryKeys.AIChatGrant(sessionId) });
  queryClient.invalidateQueries({ queryKey: queryKeys.AIChatSession(sessionId) });
};

const invalidateAccessRequestState = (queryClient, sessionId) => {
  if (!sessionId) return;
  queryClient.invalidateQueries({ queryKey: queryKeys.AIChatAccessRequests(sessionId) });
  queryClient.invalidateQueries({ queryKey: queryKeys.AIChatGrant(sessionId) });
  queryClient.invalidateQueries({ queryKey: queryKeys.AIChatSession(sessionId) });
  queryClient.invalidateQueries({ queryKey: queryKeys.AIChatMessages(sessionId, 1) });
};

const fetchAIChatAccessRequests = async ({ queryKey }) => {
  const [, , sessionId] = queryKey;
  const { data } = await axiosInstance.get(endPoints.AIChatAccessRequests(sessionId));
  return data?.data || [];
};

export const useChatAccessRequests = (sessionId, enabled = true) =>
  useQuery({
    queryKey: queryKeys.AIChatAccessRequests(sessionId),
    queryFn: fetchAIChatAccessRequests,
    enabled: !!sessionId && enabled,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

export const useCreateChatAccessRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ sessionId, payload }) => {
      const { data } = await axiosInstance.post(
        endPoints.AIChatAccessRequests(sessionId),
        payload,
      );
      return data?.data;
    },
    onSuccess: (_data, variables) =>
      invalidateAccessRequestState(queryClient, variables?.sessionId),
  });
};

export const useCancelChatAccessRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ requestId, sessionId }) => {
      const { data } = await axiosInstance.post(
        endPoints.AIChatAccessRequestCancel(requestId),
        {},
      );
      return { ...data?.data, sessionId };
    },
    onSuccess: (data) => invalidateAccessRequestState(queryClient, data?.sessionId),
  });
};

export const useApproveChatAccessRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (requestId) => {
      const { data } = await axiosInstance.post(
        endPoints.AIChatAccessRequestApprove(requestId),
        {},
      );
      return data?.data;
    },
    onSuccess: (data) => {
      invalidateAccessRequestState(queryClient, data?.session);
      queryClient.invalidateQueries({ queryKey: ["notifications", "list"] });
    },
  });
};

export const useDenyChatAccessRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ requestId, owner_note = "" }) => {
      const { data } = await axiosInstance.post(
        endPoints.AIChatAccessRequestDeny(requestId),
        { owner_note },
      );
      return data?.data;
    },
    onSuccess: (data) => {
      invalidateAccessRequestState(queryClient, data?.session);
      queryClient.invalidateQueries({ queryKey: ["notifications", "list"] });
    },
  });
};

export const useCreateAIChatGrant = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ sessionId, payload }) => {
      const { data } = await axiosInstance.post(endPoints.AIChatGrant(sessionId), payload);
      return data?.data;
    },
    onSuccess: (_data, variables) => invalidateGrantState(queryClient, variables?.sessionId),
  });
};

export const usePatchAIChatGrant = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ sessionId, payload }) => {
      const { data } = await axiosInstance.patch(endPoints.AIChatGrant(sessionId), payload);
      return data?.data;
    },
    onSuccess: (_data, variables) => invalidateGrantState(queryClient, variables?.sessionId),
  });
};

export const useRevokeAIChatGrant = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ sessionId, reason = "" }) => {
      const { data } = await axiosInstance.post(endPoints.AIChatGrantRevoke(sessionId), { reason });
      return data?.data;
    },
    onSuccess: (_data, variables) => invalidateGrantState(queryClient, variables?.sessionId),
  });
};

export const useRegenerateSummary = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId) => {
      const { data } = await axiosInstance.post(endPoints.AIChatSummaryRegenerate(sessionId), {});
      return data?.data;
    },
    onSuccess: (_data, sessionId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.AIChatSession(sessionId) });
    },
  });
};

export const useClearSummary = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId) => {
      const { data } = await axiosInstance.post(endPoints.AIChatSummaryClear(sessionId), {});
      return data?.data;
    },
    onSuccess: (_data, sessionId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.AIChatSession(sessionId) });
    },
  });
};

export const useMessageCitations = (sessionId, messageId, enabled = true) =>
  useQuery({
    queryKey: queryKeys.AIChatMessageCitations(sessionId, messageId),
    queryFn: async () => {
      const { data } = await axiosInstance.get(endPoints.AIChatMessageCitations(sessionId, messageId));
      return data?.data;
    },
    enabled: !!sessionId && !!messageId && enabled,
    staleTime: 60 * 1000,
  });
